import React, { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Car, ArrowLeft, Plug, Zap, Clock, Euro, Navigation2, MapPin } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";
import { SeoFooter } from "@/components/SeoFooter";
import { FaqSection } from "@/components/FaqSection";
import { useSeo, useJsonLd, ogImageUrl } from "@/lib/seo";
import { findCity, CITY_PATH } from "@/lib/cities";
import { cityCoords, CHARGING_PATH } from "@/lib/cityCoords";
import { chargingFaq } from "@/lib/faq";
import { fetchChargingStations } from "@/lib/api";

export default function ChargingPage() {
  const { slug } = useParams();
  const city = findCity(slug);
  const coords = city ? cityCoords(city.slug) : null;
  const ok = city && coords;
  const faqItems = ok ? chargingFaq(city) : [];

  useSeo(
    ok ? `Recharge voiture sans permis ${city.prep} : bornes compatibles | vspo` : "vspo",
    ok
      ? `Où recharger une voiture sans permis électrique ${city.prep} ? Bornes de recharge compatibles (prise domestique E/F, Type 2), puissance, tarifs et horaires, avec vspo.`
      : "",
    ok ? `${CHARGING_PATH}/${city.slug}` : undefined,
    ok ? ogImageUrl({ title: `Recharge VSP`, subtitle: city.name }) : undefined
  );

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  useJsonLd(
    "charging-jsonld",
    ok
      ? [
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Accueil", item: `${origin}/` },
              { "@type": "ListItem", position: 2, name: `Recharge VSP ${city.prep}`, item: `${origin}${CHARGING_PATH}/${city.slug}` },
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqItems.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ]
      : null
  );

  const [state, setState] = useState({ loading: true, error: false, stations: [] });

  useEffect(() => {
    if (!ok) return undefined;
    let cancelled = false;
    setState({ loading: true, error: false, stations: [] });
    fetchChargingStations({ lat: coords.lat, lon: coords.lon, socket: "all", radius_km: 8, limit: 10 })
      .then((d) => { if (!cancelled) setState({ loading: false, error: false, stations: (d && d.stations) || [] }); })
      .catch(() => { if (!cancelled) setState({ loading: false, error: true, stations: [] }); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (!ok) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen w-full overflow-y-auto bg-background">
      <header className="sticky top-0 z-10 border-b border-border bg-card/95 backdrop-blur-md">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground"><Car className="h-5 w-5" /></span>
            <div className="leading-tight">
              <div className="font-display text-lg font-bold text-foreground">vspo</div>
              <div className="text-[11px] text-muted-foreground">GPS voiture sans permis · 45 km/h</div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <ThemeToggle />
            <Link to="/" className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground" data-testid="charging-open-app">
              <ArrowLeft className="h-4 w-4" /> Ouvrir le GPS
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 pb-16 pt-6">
        <nav className="mb-3 text-xs text-muted-foreground" aria-label="Fil d'ariane">
          <Link to="/" className="hover:text-primary">Accueil</Link> ·{" "}
          <Link to={`${CITY_PATH}/${city.slug}`} className="hover:text-primary">{city.name}</Link> ·{" "}
          <span className="text-foreground">Recharge</span>
        </nav>

        <div className="mb-2 flex items-center gap-2">
          <Plug className="h-5 w-5 text-primary" />
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Recharge VSP</span>
        </div>
        <h1 className="font-display text-3xl font-bold text-foreground" data-testid="charging-title">
          Recharge voiture sans permis {city.prep}
        </h1>
        <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">
          Où recharger une <strong>voiture sans permis électrique {city.prep}</strong> ? La plupart des
          voiturettes (Citroën Ami…) se rechargent sur une <strong>prise domestique 220 V</strong>, mais
          certaines bornes publiques proposent une prise <strong>E/F</strong> ou <strong>Type 2</strong> compatible.
          Voici les bornes les plus proches du centre-ville.
        </p>

        <section className="mt-6">
          <h2 className="font-display text-xl font-bold text-foreground">Bornes près {city.prep}</h2>
          <div className="mt-3 space-y-3" data-testid="charging-list">
            {state.loading && (
              [1, 2, 3].map((i) => (
                <div key={i} className="h-20 animate-pulse rounded-2xl border border-border bg-card" />
              ))
            )}
            {!state.loading && state.error && (
              <div className="rounded-2xl border border-border bg-card p-4 text-sm text-muted-foreground" data-testid="charging-error">
                Le service de bornes est momentanément indisponible. Vous pouvez réessayer plus tard ou
                ouvrir le GPS pour chercher une borne sur votre trajet.
              </div>
            )}
            {!state.loading && !state.error && state.stations.length === 0 && (
              <div className="rounded-2xl border border-border bg-card p-4 text-sm text-muted-foreground" data-testid="charging-empty">
                Aucune borne compatible trouvée à proximité immédiate. Essayez depuis le GPS en élargissant
                la zone, ou rechargez sur une prise domestique.
              </div>
            )}
            {!state.loading && !state.error && state.stations.map((s) => (
              <div key={s.id} className="rounded-2xl border border-border bg-card p-4" data-testid="charging-station">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-semibold text-foreground">{s.name || "Borne de recharge"}</div>
                    {s.address && (
                      <div className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                        <MapPin className="h-3.5 w-3.5" /> {s.address}
                      </div>
                    )}
                  </div>
                  {s.distance_m != null && (
                    <span className="shrink-0 rounded-full bg-secondary px-2 py-1 text-xs text-muted-foreground">
                      {s.distance_m < 1000 ? `${s.distance_m} m` : `${(s.distance_m / 1000).toFixed(1)} km`}
                    </span>
                  )}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                  {(s.sockets || []).map((sk) => (
                    <span key={sk} className="inline-flex items-center gap-1"><Plug className="h-3.5 w-3.5 text-primary" /> {sk}</span>
                  ))}
                  {s.power_kw != null && <span className="inline-flex items-center gap-1"><Zap className="h-3.5 w-3.5 text-primary" /> {s.power_kw} kW</span>}
                  <span className="inline-flex items-center gap-1"><Euro className="h-3.5 w-3.5 text-primary" /> {s.free ? "Gratuit" : "Payant"}</span>
                  {s.open_247 && <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5 text-primary" /> 24/7</span>}
                </div>
              </div>
            ))}
          </div>
        </section>

        <div className="mt-10 rounded-2xl border border-border bg-card p-5">
          <h2 className="font-display text-lg font-bold text-foreground">Rechargez malin avec vspo</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Dans le GPS, filtrez les bornes par type de prise et ajoutez un arrêt recharge sur votre trajet.
          </p>
          <Link to="/" className="mt-3 inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground" data-testid="charging-cta">
            <Navigation2 className="h-4 w-4" /> Ouvrir le GPS
          </Link>
        </div>

        <section className="mt-8">
          <div className="flex flex-wrap gap-2">
            <Link to={`${CITY_PATH}/${city.slug}`} className="text-sm text-primary hover:underline" data-testid="charging-city-link">
              GPS VSP {city.prep}
            </Link>
            <Link to="/conseils/recharge-voiture-sans-permis-electrique" className="text-sm text-primary hover:underline">
              Guide recharge VSP électrique
            </Link>
          </div>
        </section>

        <FaqSection items={faqItems} />
      </main>

      <SeoFooter />
    </div>
  );
}
